import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Code, Zap, Brain, ShoppingCart, Sparkles, Star, Rocket, Moon, Sun, Calculator } from 'lucide-react';
import Tilt from 'react-parallax-tilt';
import darkLogo from 'assets/images.png';
import lightLogo from 'assets/Lu.png';

const Home: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const isServicesInView = useInView(servicesRef, { once: true, margin: "-100px" });
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };
    
    // Initial check
    checkDarkMode();
    
    // Observe for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  // Particle animation effect
  useEffect(() => {
    const canvas = document.getElementById('particles') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
    }> = [];

    for (let i = 0; i < 50; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(59, 130, 246, ${particle.opacity})`;
        ctx.fill();
      });

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    {
      icon: Zap,
      title: 'IT Automation',
      description: 'Streamline your operations with intelligent automation solutions',
      gradient: 'from-blue-500 to-cyan-500',
      delay: 0.1
    },
    {
      icon: Code,
      title: 'Web & App Development',
      description: 'Custom web and mobile applications tailored to your needs',
      gradient: 'from-purple-500 to-pink-500',
      delay: 0.2
    },
    {
      icon: Brain,
      title: 'AI Solutions',
      description: 'Harness the power of artificial intelligence for your business',
      gradient: 'from-green-500 to-emerald-500',
      delay: 0.3
    },
    {
      icon: ShoppingCart,
      title: 'E-commerce',
      description: 'Global marketplace solutions and product management',
      gradient: 'from-orange-500 to-red-500',
      delay: 0.4
    }
  ];

  const features = [
    {
      icon: Sparkles,
      title: 'Innovation First',
      description: 'We stay ahead of technology trends to deliver cutting-edge solutions'
    },
    {
      icon: Star,
      title: 'Global Reach',
      description: 'From local startups to international marketplaces, we scale with you'
    },
    {
      icon: Rocket,
      title: 'Expert Team',
      description: 'Skilled professionals dedicated to your success'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden">
      {/* Animated Background */}
      <canvas
        id="particles"
        className="fixed inset-0 pointer-events-none opacity-30 dark:opacity-20"
      />

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-end justify-center pb-20">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <img
            src={isDarkMode ? darkLogo : lightLogo}
            alt="Lunexa Background"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ backgroundColor: isDarkMode ? '#000000' : '#ffffff' }}
            onError={(e) => {
              console.error('Failed to load image:', e);
            }}
          />
        </div>

      </section>

      {/* Services Overview */}
      <section ref={servicesRef} className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            animate={isServicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Our Core Services
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Comprehensive solutions for modern businesses
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 50 }}
                  animate={isServicesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: service.delay }}
                >
                  <Tilt
                    tiltMaxAngleX={10}
                    tiltMaxAngleY={10}
                    perspective={1000}
                    scale={1.02}
                    transitionSpeed={2000}
                    gyroscope={true}
                  >
                    <div className="group relative h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500 hover:bg-white/20 dark:hover:bg-gray-800/20">
                      {/* Gradient Border */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${service.gradient} opacity-0 group-hover:opacity-20 rounded-3xl transition-opacity duration-500`} />
                      
                      {/* Icon */}
                      <motion.div
                        className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${service.gradient} mb-6 shadow-lg`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </motion.div>

                      <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-primary-600 group-hover:to-purple-600 dark:group-hover:from-cyan-400 dark:group-hover:to-purple-400 transition-all duration-300">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {service.description}
                      </p>

                      {/* Hover Effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl"
                      />
                    </div>
                  </Tilt>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Highlights */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                Why Choose Lunexa?
              </h2>
              
              <div className="space-y-8">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <motion.div
                      key={feature.title}
                      className="flex items-start group"
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.2 }}
                      viewport={{ once: true }}
                    >
                      <motion.div
                        className="flex-shrink-0 p-3 bg-gradient-to-r from-primary-500 to-purple-500 dark:from-cyan-500 dark:to-purple-500 rounded-2xl mr-6 shadow-lg group-hover:shadow-neon transition-all duration-300"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </motion.div>
                      <div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Tilt
                tiltMaxAngleX={5}
                tiltMaxAngleY={5}
                perspective={1000}
                scale={1.02}
              >
                <div className="relative backdrop-blur-xl bg-gradient-to-br from-white/20 to-white/10 dark:from-gray-800/20 dark:to-gray-800/10 border border-white/30 dark:border-gray-700/30 rounded-3xl p-10 shadow-glass">
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-purple-500/10 dark:from-cyan-500/10 dark:to-purple-500/10 rounded-3xl" />
                  
                  <div className="relative z-10">
                    <motion.h3
                      className="text-3xl font-bold mb-6 bg-gradient-to-r from-primary-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400 bg-clip-text text-transparent"
                      animate={{ 
                        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                      }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      Ready to Get Started?
                    </motion.h3>
                    <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                      Join hundreds of satisfied clients who trust Lunexa for their technology needs. 
                      Let's build something amazing together.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Link
                          to="/contact"
                          className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-purple-600 dark:from-cyan-500 dark:to-purple-500 text-white font-semibold rounded-2xl shadow-lg hover:shadow-neon transition-all duration-300"
                        >
                          <span>Start Your Project</span>
                          <motion.div
                            className="ml-3"
                            animate={{ x: [0, 5, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          >
                            <ArrowRight size={20} />
                          </motion.div>
                        </Link>
                      </motion.div>
                      
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Link
                          to="/listing-calculator"
                          className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 dark:from-green-400 dark:to-emerald-400 text-white font-semibold rounded-2xl shadow-lg hover:shadow-neon transition-all duration-300"
                        >
                          <span>Price Calculator</span>
                          <motion.div
                            className="ml-3"
                            animate={{ rotate: [0, 360] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                          >
                            <Calculator size={20} />
                          </motion.div>
                        </Link>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </Tilt>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;